import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

// Mirrors apps/api/src/lib/import/import-history.ts's shapes 1:1 (raw
// Zod-free objects, same convention as leads-import.ts/sales-import.ts). TASK-12.

export interface ImportBatchHistoryRow {
  id: number;
  label: string;
  status: string;
  created_at: string;
  created_by: string | null;
  counts: { imported?: number; skipped_duplicates?: number };
}

const historyKey = (projectId: number) => ["admin", "projects", "import-history", projectId] as const;

export function useImportHistory(projectId: number) {
  return useQuery<{ batches: ImportBatchHistoryRow[] }>({
    queryKey: historyKey(projectId),
    queryFn: () => api(`/admin/projects/${projectId}/import/history`),
    staleTime: 10_000,
  });
}

export interface UndoImportResponse {
  ok: true;
  batch_id: number;
  label: string;
  deleted: { processed_events: number; events: number; visitor_keys: number; visitors: number };
  /** True when a previous undo (or an empty batch) already removed everything. */
  already_undone: boolean;
  bigquery: { affected: boolean; note: string };
}

export function useUndoImport(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchId: number) =>
      api<UndoImportResponse>(`/admin/projects/${projectId}/import/undo`, {
        method: "POST",
        body: JSON.stringify({ batchId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: historyKey(projectId) });
    },
  });
}
